import React from "react";
import _ from "lodash";
import { FormattedMessage } from "react-intl";

import IFilter from "models/IFilter";
import Input from "components/Input";
import Select from "components/Select";
import DetailView from "components/DetailView";
import RangeDatepicker from "components/RangeDatepicker";
import ToggleSwitch from "components/ToggleSwitch";
import Button from "components/Button";
import FilterGenerator from "helpers/filter";
import withFilters from "helpers/withFilters";
import { CUSTOM_SETTINGS } from "constants/.";

interface IProps extends IFilter {
  statusOptions: any[];
  typeOptions: any[];
}

class OrderSearch extends React.Component<IProps> {
  public static getInputQuery = (value) => {
    const Q = new FilterGenerator();

    if (value) {
      Q.and("Number", value);
      Q.or("OtherRelation/Name", value);
      !isNaN(value) && Q.or("HarvestYear", value, "number");
      Q.addText(" or (");
      Q.without("Lines", value, "array", "Item/CultivarCode");
      Q.or("Lines", value, "array", "Item/Description");
      Q.addText(")");
    }

    return Q.query;
  };

  public static getFilterQuery = (f) => {
    const Q = new FilterGenerator();

    if (!_.isEmpty(f.versionDate)) {
      Q.and("VersionDate", f.versionDate, "dateRange");
    }
    if (!_.isEmpty(f.deliveryEnd)) {
      Q.and("DeliveryPeriodEnd", f.deliveryEnd, "dateRange");
    }
    if (!_.isEmpty(f.orderDate)) {
      Q.and("Date", f.orderDate, "dateRange");
    }
    Q.and("Number", f.orderNumber);
    Q.and("OtherRelation/Name", f.counterpartyName);
    Q.and("OtherRelation/Number", f.counterpartyNumber);
    Q.and("Seller/Name", f.sellerName);
    Q.and("Supplier/Name", f.supplierName);
    Q.and("VersionNumber", f.versionNumber, "string");
    Q.and("HarvestYear", f.harvestYear, "number");
    Q.and("StateId", f.status, "select");
    Q.and("RoleId", f.role, "select");
    Q.and("TypeId", f.type, "select");
    Q.and("Bio", f.bio, "boolean");
    if (f.description) {
      const checkQuery = Q.query ? true : false;
      checkQuery && Q.addText("and (");
      Q.without("Lines", f.description, "array", "Item/CultivarCode");
      Q.or("Lines", f.description, "array", "Item/Description");
      checkQuery && Q.addText(")");
    }

    return Q.query;
  };

  public maskOnInput = (e) => {
    const { filter, handleFilterChange } = this.props;
    const value = e.target.validity.valid
      ? e.target.value
      : _.get(filter, e.target.name, "");

    handleFilterChange(value, e.target.name);
  };

  public render = () => {
    const {
      filter,
      handleFilterChange,
      generateFilter,
      resetFilter,
      roleIds,
      statusOptions,
      typeOptions,
    } = this.props;

    return (
      <DetailView
        pageName={CUSTOM_SETTINGS.orders}
        renderExpanded={() => (
          <div>
            <div className="filter-row">
              <div className="inner-block">
                <FormattedMessage id="PURCHASE_TYPE" tagName="label" />
                <Select
                  options={roleIds}
                  selectedOption={filter.role}
                  onChange={(value) => handleFilterChange(value, "role")}
                  className="min-width-select-forms"
                />
              </div>
              <div className="inner-block">
                <FormattedMessage id="ORDERS_TYPE" tagName="label" />
                <Select
                  options={typeOptions}
                  selectedOption={filter.type}
                  onChange={(value) => handleFilterChange(value, "type")}
                  className="min-width-select-forms"
                />
              </div>
            </div>
            <div className="filter-row">
              <RangeDatepicker
                name="versionDate"
                label="ORDERS_LAST_DATE"
                value={filter.versionDate}
                handleRangeChange={handleFilterChange}
              />
              <RangeDatepicker
                name="deliveryEnd"
                label="DELIVERIES_END_DATE"
                value={filter.deliveryEnd}
                handleRangeChange={handleFilterChange}
              />
            </div>
            <div className="filter-row">
              <Input
                label="GROWER"
                value={filter.sellerName}
                onChange={(e) =>
                  handleFilterChange(e.target.value, "sellerName")
                }
              />
              <Input
                label="PREFERRED_GROWER"
                value={filter.supplierName}
                onChange={(e) =>
                  handleFilterChange(e.target.value, "supplierName")
                }
              />
            </div>
            <div className="filter-row">
              <RangeDatepicker
                name="orderDate"
                label="ORDERS_DATE"
                value={filter.orderDate}
                handleRangeChange={handleFilterChange}
              />
              <Input
                label="VERSION_NR"
                pattern="[0-9]*"
                value={filter.versionNumber}
                name="versionNumber"
                onChange={this.maskOnInput}
              />
            </div>
            <div className="filter-row">
              <ToggleSwitch
                label="DELIVERIES_BIO"
                on={filter.bio}
                onClick={() => handleFilterChange(!filter.bio, "bio")}
              />
            </div>
          </div>
        )}
      >
        <div className="filter-row">
          <Input
            label="ORDERS_NUMBER"
            value={filter.orderNumber}
            onChange={(e) => handleFilterChange(e.target.value, "orderNumber")}
          />
          <Input
            label="COUNTERPARTY_NAME"
            value={filter.counterpartyName}
            onChange={(e) =>
              handleFilterChange(e.target.value, "counterpartyName")
            }
          />
        </div>
        <div className="filter-row">
          <Input
            label="HARVEST_YEAR"
            pattern="[0-9]*"
            maxLength={4}
            onChange={this.maskOnInput}
            value={filter.harvestYear}
            name="harvestYear"
          />
          <Input
            label="COUNTERPARTY_NUMBER"
            value={filter.counterpartyNumber}
            onChange={(e) =>
              handleFilterChange(e.target.value, "counterpartyNumber")
            }
          />
        </div>
        <div className="filter-row">
          <div className="inner-block">
            <FormattedMessage id="STATUS" tagName="label" />
            <Select
              options={statusOptions}
              selectedOption={filter.status}
              onChange={(value) => handleFilterChange(value, "status")}
              className="min-width-select-forms"
            />
          </div>
          <Input
            label="ARTICLE_DESCRIPTION"
            value={filter.description}
            onChange={(e) => handleFilterChange(e.target.value, "description")}
          />
        </div>
        <div className="search-btn">
          <Button onClick={resetFilter}>RESET</Button>
          <Button kind="primary" onClick={generateFilter}>
            SEARCH
          </Button>
        </div>
      </DetailView>
    );
  };
}

export default withFilters({
  placeholder: "ORDERS_SEARCH",
  pageName: CUSTOM_SETTINGS.orders,
})(OrderSearch);
